import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_URL, DOCS_URL } from '../config';

const DriverForm = ({ onSchemaSubmit }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        nom: '',
        adresse: '',
        telephone: '',
        typeChauffeur: '',
        tarif: '',
    });
    const [downloaded, setDownloaded] = useState(false); // État pour forcer le téléchargement

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ 
            ...formData, 
            [name]: value,
            // Reset tarif if type changes
            ...(name === 'typeChauffeur' ? { tarif: '', downloaded: false } : {})
        });
        if (name === 'typeChauffeur') setDownloaded(false); // Reset download state on type change
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!downloaded) {
            alert("Veuillez d'abord télécharger votre contrat (Étape 2) pour pouvoir soumettre la demande.");
            return;
        }
        try {
            const response = await axios.post(`${API_URL}/demandes`, formData);
            if (response.data.success) {
                if (onSchemaSubmit) onSchemaSubmit();
                navigate('/documents');
            }
        } catch (err) {
            alert("Erreur lors de l'envoi du formulaire.");
        }
    };

    const renderDynamicOptions = () => {
        const DownloadButton = ({ fileName, label }) => (
            <div className="mb-4 p-4 bg-white rounded-4 border border-warning-subtle shadow-sm mt-3 hover-lift transition-all">
                <div className="d-flex align-items-center gap-3 mb-3">
                    <div className="bg-warning bg-opacity-10 p-3 rounded-circle text-warning">
                        <i className="bi bi-file-earmark-text-fill fs-4"></i>
                    </div>
                    <div>
                        <p className="mb-0 fw-bold text-dark">Étape 2 : Signature du contrat</p>
                        <small className="text-muted">Document officiel requis pour validation</small>
                    </div>
                </div>
                <a 
                    href={`${DOCS_URL}/${encodeURIComponent(fileName)}?download=true`} 
                    download 
                    onClick={() => setDownloaded(true)}
                    className={`btn ${downloaded ? 'btn-success' : 'btn-dark'} w-100 d-inline-flex align-items-center justify-content-center gap-2 px-4 py-3 rounded-3 shadow-sm fw-bold transition-all`}
                >
                    <i className={`bi ${downloaded ? 'bi-check-circle-fill' : 'bi-download'}`}></i>
                    {downloaded ? 'Contrat téléchargé avec succès !' : `Télécharger ${label}`}
                </a>
            </div>
        );

        const Step1Download = ({ fileName, label }) => (
            <div className="mb-4 p-4 bg-white rounded-4 border border-info-subtle shadow-sm mt-3 animate__animated animate__fadeIn hover-lift transition-all">
                <div className="d-flex align-items-center gap-3 mb-3">
                    <div className="bg-info bg-opacity-10 p-3 rounded-circle text-info">
                        <i className="bi bi-info-circle-fill fs-4"></i>
                    </div>
                    <div>
                        <p className="mb-0 fw-bold text-dark">Étape 1 : Modalités de fonctionnement</p>
                        <small className="text-muted">Prenez connaissance de nos conditions</small>
                    </div>
                </div>
                <a 
                    href={`${DOCS_URL}/${encodeURIComponent(fileName)}?download=true`} 
                    download 
                    className="btn btn-outline-info w-100 d-inline-flex align-items-center justify-content-center gap-2 px-4 py-3 rounded-3 shadow-sm fw-bold transition-all"
                >
                    <i className="bi bi-download"></i>
                    Télécharger {label}
                </a>
            </div>
        );

        const PriceOptions = ({ options }) => (
            <div className="mb-4 p-4 bg-light rounded-4 border-0 animate__animated animate__fadeIn">
                <label className="form-label fw-bold text-dark d-flex align-items-center gap-2 mb-3">
                    <i className="bi bi-tags-fill text-warning"></i>
                    Options de Prix
                </label>
                <div className="row g-3">
                    {options.map(opt => (
                        <div className="col-12" key={opt}>
                            <input 
                                className="btn-check" 
                                type="radio" 
                                name="tarif" 
                                value={opt} 
                                checked={formData.tarif === opt}
                                onChange={handleChange} 
                                required 
                                id={opt} 
                            />
                            <label className="btn btn-outline-dark w-100 py-3 text-start px-4 rounded-3 border-2 fw-semibold d-flex justify-content-between align-items-center" htmlFor={opt}>
                                {opt}
                                {formData.tarif === opt && <i className="bi bi-check-circle-fill text-warning"></i>}
                            </label>
                        </div>
                    ))}
                </div>
            </div>
        );

        switch (formData.typeChauffeur) {
            case 'Taxi':
                return (
                    <div className="animate__animated animate__fadeIn">
                        <Step1Download 
                            fileName="Charte de Fonctionnement taxi.pdf" 
                            label="la Charte de Fonctionnement" 
                        />
                        <DownloadButton 
                            fileName="CONTRAT PAXDRIVER TAXI.pdf" 
                            label="le Contrat Taxi" 
                        />
                    </div>
                );
            case 'Particulier':
                return (
                    <div className="animate__animated animate__fadeIn">
                        <PriceOptions options={['Chauffeur Standard 250$', 'Chauffeur Standard Premium 325$', 'Ouvert à discussion sur le prix']} />
                        <Step1Download 
                            fileName="Modalités de nos chauffeurs.pdf" 
                            label="les Modalités" 
                        />
                        <DownloadButton 
                            fileName="Contrat paxdriver (particulier).pdf" 
                            label="le Contrat Particulier" 
                        />
                    </div>
                );
            case 'Entreprise':
                return (
                    <div className="animate__animated animate__fadeIn">
                        <PriceOptions options={['Supérieur à 400$', 'Ouvert à discussion sur le prix']} />
                        <Step1Download 
                            fileName="Modalités de nos chauffeurs.pdf" 
                            label="les Modalités" 
                        />
                        <DownloadButton 
                            fileName="Contrat paxdriver (Entreprise, Organisation et Ambassade).pdf" 
                            label="le Contrat Entreprise" 
                        />
                    </div>
                );
            case 'Organisation':
                return (
                    <div className="animate__animated animate__fadeIn">
                        <PriceOptions options={['supérieur à 600$', 'Ouvert à discussion sur le prix']} />
                        <Step1Download 
                            fileName="Modalités de nos chauffeurs.pdf" 
                            label="les Modalités" 
                        />
                        <DownloadButton 
                            fileName="Contrat paxdriver (Entreprise, Organisation et Ambassade).pdf" 
                            label="le Contrat Organisation" 
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="card border-0 shadow-lg rounded-4 p-4 p-md-5 mt-n5">
            <div className="text-center mb-5">
                <div className="badge bg-warning-subtle text-warning px-3 py-2 rounded-pill mb-3 fw-bold border border-warning-subtle">
                    <i className="bi bi-shield-check me-1"></i> Formulaire Sécurisé
                </div>
                <h2 className="fw-bold text-dark text-uppercase mb-2" style={{ letterSpacing: '1px' }}>Demande de Chauffeur</h2>
                <p className="text-muted mx-auto" style={{ maxWidth: '400px' }}>
                    Rejoignez le réseau de transport premium <strong>Pax Driver</strong>. 
                    Veuillez remplir les informations ci-dessous.
                </p>
            </div>

            <form onSubmit={handleSubmit} className="needs-validation">
                <div className="mb-4">
                    <label className="form-label fw-bold small text-uppercase text-muted mb-2">Informations Générales</label>
                    <div className="form-floating mb-3">
                        <input type="text" name="nom" className="form-control bg-light border-0 px-4" onChange={handleChange} required placeholder="Ex: Jean Dupont" id="nomInput" />
                        <label htmlFor="nomInput" className="text-muted px-4">Nom complet / Entreprise / Organisation</label>
                    </div>
                </div>

                <div className="row g-3 mb-4">
                    <div className="col-md-6">
                        <div className="form-floating">
                            <input type="text" name="adresse" className="form-control bg-light border-0 px-4" onChange={handleChange} required placeholder="Votre adresse" id="adresseInput" />
                            <label htmlFor="adresseInput" className="text-muted px-4">Adresse</label>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-floating">
                            <input type="tel" name="telephone" className="form-control bg-light border-0 px-4" onChange={handleChange} required placeholder="+243..." id="telInput" />
                            <label htmlFor="telInput" className="text-muted px-4">Téléphone</label>
                        </div>
                    </div>
                </div>

                <div className="mb-5">
                    <label className="form-label fw-bold small text-uppercase text-muted mb-3">Profil du chauffeur</label>
                    <div className="input-group input-group-lg">
                        <span className="input-group-text bg-warning border-warning text-dark"><i className="bi bi-person-badge-fill"></i></span>
                        <select name="typeChauffeur" className="form-select border-2 border-warning fw-bold" onChange={handleChange} required>
                            <option value="">Sélectionnez votre profil...</option>
                            <option value="Taxi">Chauffeur Taxi</option>
                            <option value="Particulier">Chauffeur Particulier</option>
                            <option value="Entreprise">Entreprise / Société</option>
                            <option value="Organisation">Organisation / Ambassade</option>
                        </select>
                    </div>
                </div>

                {renderDynamicOptions()}

                <button 
                    type="submit" 
                    className="btn btn-warning btn-lg w-100 fw-bold text-uppercase py-3 rounded-pill shadow-sm mt-4"
                >
                    Soumettre la demande
                </button>
            </form>
        </div>
    );
};

export default DriverForm;
